import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import DefaultButton from '../../components/Button/Button'
import FormInput from '../../components/Input'
import { ResidentTypeDTO } from '../../interfaces/resident'
import ResidentService from '../../api/resident-service'
import { showToast } from '../../utils/toast'
import { createResidentTypeSchema } from '../../validations/createResidentType'

interface EditResidentTypeProps {
  residentType: ResidentTypeDTO
  estateId: string
  onClose: () => void
  refetch: () => void
}

function EditResidentType({
  residentType,
  estateId,
  onClose,
  refetch,
}: EditResidentTypeProps) {
  const [isLoading, setIsLoading] = useState(false)

  const formik = useFormik({
    initialValues: {
      title: '',
      price: 0,
      description: '',
    },
    validationSchema: createResidentTypeSchema,
    onSubmit: async (values) => {
      if (isLoading) return

      setIsLoading(true)
      try {
        if (!residentType.id) {
          throw new Error('Resident Type ID is missing.')
        }

        const updatedValues = {
          ...values,
          price:
            typeof values.price === 'string' ? parseFloat(values.price) : values.price,
        }

        await ResidentService.updateResidentType(
          estateId,
          String(residentType.id),
          updatedValues
        )

        showToast({
          title: 'Success',
          description: 'Resident Type updated successfully.',
          status: 'success',
        })
        refetch()
        onClose()
      } catch (error: any) {
        showToast({
          title: 'Error',
          description: error.message || 'Failed to update resident type.',
          status: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    },
  })

 
  useEffect(() => {
    formik.setValues({
      title: residentType.title || '',
      price:
        typeof residentType.price === 'string'
          ? Number(residentType.price)
          : residentType.price || 0,
      description: residentType.description || '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residentType.id])

  return (
    <div className="py-10 px-12">
      <form onSubmit={formik.handleSubmit}>
        <div className="flex gap-8 mb-5">
          <FormInput
            label="Resident type"
            placeholder="e.g Bungalow"
            size="lg"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            error={formik.errors.title}
          />
          <FormInput
            label="Levy fee in Naira"
            placeholder="e.g 1000"
            size="lg"
            name="price"
            type="number"
            value={formik.values.price}
            onChange={(e) => formik.setFieldValue('price', Number(e.target.value))}
            error={formik.errors.price}
          />
        </div>
        <div className="flex flex-col gap-2 mb-5">
          <label htmlFor="description" className="text-lg font-medium">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            rows={4}
            className="border border-solid rounded-lg p-2"
            value={formik.values.description}
            onChange={formik.handleChange}
            aria-label="Description field"
          ></textarea>
          {formik.errors.description && (
            <p className="text-red-500 text-sm">{formik.errors.description}</p>
          )}
        </div>
        <DefaultButton
          isLoading={isLoading}
          loadingText="Saving..."
          type="submit"
          className="float-right mt-5"
          disabled={isLoading || !(formik.dirty && formik.isValid)}
        >
          Save Changes
        </DefaultButton>
      </form>
    </div>
  )
}

export default EditResidentType
